'use client'

import {
  ArrowUpRightIcon,
  CarIcon,
  CaretLeftIcon,
  CaretRightIcon,
  FirstAidKitIcon,
  GraduationCapIcon,
  MagnifyingGlassIcon,
  MotorcycleIcon,
  TrafficConeIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { PageInfo } from '@/src/@types/models'
import { FlchLocationsBlock } from '@/src/@types/statamic'
import { flchUrl, get } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import TrailerIcon from '@/src/components/core/icons/TrailerIcon'
import Picture from '@/src/components/elements/Picture'
import { useSearchDialog } from '@/src/components/flch/components/SearchDialog'
import Container from '@/src/components/layout/Container'

export async function getStaticProps({
  title,
  text,
  locations_set,
}: FlchLocationsBlock) {
  const locationIds = locations_set
    .map((locations) => locations.location.value)
    .join(',')
  const locations = await get<FahrlehrerCh.LocationInfo[]>(
    `${flchUrl('locations')}?filter[id]=${locationIds}`
  )

  return {
    title,
    text,
    locations: (locations ?? []).map((location) => ({
      ...location,
      image: locations_set.find(
        (loc) => loc.location.value === String(location.id)
      )?.image,
    })),
  }
}

export default function Component({
  title,
  text,
  locations,
  pageInfo,
}: Awaited<ReturnType<typeof getStaticProps>> & {
  pageInfo: PageInfo
}) {
  const openSearch = useSearchDialog()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    skipSnaps: true,
    breakpoints: {
      '(min-width: 1024px)': {
        active: locations.length > 4 ? true : false,
        align: 'start',
      },
    },
  })
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  if (!locations.length) return null

  return (
    <div
      className={clsx({
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        <div className="flex max-w-3xl flex-col gap-2 md:gap-4">
          <h2 className="text-4xl font-black text-balance md:text-20xl">
            {title}
          </h2>
          <p className="text-gray-800 md:text-lg">{text}</p>
        </div>
      </Container>
      <div className="mt-6 overflow-hidden md:mt-10" ref={emblaRef}>
        <div className="flex">
          {locations.map((location) => (
            <div
              key={location.id}
              className="min-w-0 flex-[0_0_calc(100%-12px)] border-r border-gray-150 px-5 last:border-none min-[1342px]:first:ml-[calc((100%-1320px)/2)] min-[1342px]:last:mr-[calc((100%-1320px)/2)]! lg:flex-[0_0_calc(min(25%,1320px/4))]"
            >
              <Link href={location.url} className="group">
                <span className="relative block aspect-16/9 w-full overflow-hidden rounded-xl border border-gray-150 md:rounded-lg">
                  {location.image && (
                    <Picture
                      {...location.image}
                      alt={`BLINK Fahrschule ${location.name}`}
                      sizes="320px"
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                  <Button
                    as="span"
                    version="white-gray"
                    size="icon-small"
                    icon={<ArrowUpRightIcon weight="bold" />}
                    className="absolute top-3 right-3"
                  />
                </span>
                <h3 className="mt-4 text-lg font-black">{location.name}</h3>
              </Link>
              <p className="mt-1 text-gray-800">
                {location.bookableTeacherCount} Fahrlehrer:innen
              </p>
              <p className="mt-4 text-sm text-gray-600">Weitere Angebote</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {location.services.map((service) => (
                  <ServiceTag key={service.type} {...service} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Container>
        <div className="mt-6 flex items-center justify-between gap-4">
          <button
            onClick={openSearch}
            className="flex items-center gap-2 text-sm font-bold text-blue-700"
          >
            <MagnifyingGlassIcon size={20} weight="bold" />
            Standort suchen
          </button>
          <div className="flex justify-end gap-3">
            <Button
              size="icon-small"
              icon={<CaretLeftIcon weight="bold" />}
              onClick={() => emblaApi?.scrollPrev()}
              version="gray-outlined"
              disabled={prevDisabled}
              aria-label="Vorheriger Standort"
            />
            <Button
              size="icon-small"
              icon={<CaretRightIcon weight="bold" />}
              onClick={() => emblaApi?.scrollNext()}
              version="gray-outlined"
              disabled={nextDisabled}
              aria-label="Nächster Standort"
            />
          </div>
        </div>
      </Container>
    </div>
  )
}

function ServiceTag({ type, url }: FahrlehrerCh.LocationInfoService) {
  let label = type
  let Icon = CarIcon
  switch (type) {
    case 'car':
      label = 'Auto'
      Icon = CarIcon
      break
    case 'bike':
      label = 'Motorrad'
      Icon = MotorcycleIcon
      break
    case 'trailer':
      label = 'Anhänger'
      Icon = TrailerIcon
      break
    case 'firstaid':
      label = 'Nothilfekurs'
      Icon = FirstAidKitIcon
      break
    case 'vku':
      label = 'VKU'
      Icon = GraduationCapIcon
      break
    case 'pgs':
      label = 'PGS'
      Icon = GraduationCapIcon
      break
    case 'pgs':
      label = 'WAB'
      Icon = TrafficConeIcon
      break
  }

  return (
    <Link
      href={url}
      className="flex items-center gap-2 rounded-full border border-gray-300 py-2 pr-4 pl-3 text-sm transition-colors duration-200 hover:bg-gray-50 hover:duration-150"
    >
      <Icon size={20} weight="bold" className="text-gray-600" />
      <span className="text-gray-800">{label}</span>
    </Link>
  )
}
